const GUIDDE_SHARE_REGEX =
  /https?:\/\/app\.guidde\.com\/share\/playbooks\/([A-Za-z0-9_-]+)(?:\?[^\s]*)?/gi;

const GUIDDE_EMBED_REGEX =
  /https?:\/\/embed\.app\.guidde\.com\/playbooks\/([A-Za-z0-9_-]+)(?:\?[^\s]*)?/gi;

const SUPADEMO_URL_REGEX =
  /https?:\/\/[^\s/]+\/(demo|showcase)\/([A-Za-z0-9_-]+)(?:[^\s]*)?/gi;

/** Bare Supademo demo/showcase ids look like cuid-style strings. */
const SUPADEMO_BARE_ID_REGEX = /^[a-z0-9]{20,40}$/i;

export function extractGuiddeVideoUrl(text = "") {
  const value = String(text || "");
  const shareMatch = value.match(
    /https?:\/\/app\.guidde\.com\/share\/playbooks\/([A-Za-z0-9_-]+)/i,
  );
  if (shareMatch?.[1]) {
    return `https://app.guidde.com/share/playbooks/${shareMatch[1]}`;
  }
  const embedMatch = value.match(
    /https?:\/\/embed\.app\.guidde\.com\/playbooks\/([A-Za-z0-9_-]+)/i,
  );
  if (embedMatch?.[1]) {
    return `https://app.guidde.com/share/playbooks/${embedMatch[1]}`;
  }
  return null;
}

export function toGuiddeEmbedUrl(shareOrEmbedUrl) {
  const id = extractGuiddePlaybookId(shareOrEmbedUrl);
  if (!id) return null;
  return `https://embed.app.guidde.com/playbooks/${id}`;
}

function extractGuiddePlaybookId(url = "") {
  const value = String(url || "");
  const match =
    value.match(/\/playbooks\/([A-Za-z0-9_-]+)/i) ||
    value.match(/playbooks\/([A-Za-z0-9_-]+)/i);
  return match?.[1] || null;
}

/**
 * @returns {{ demoId: string, type: 'demo' | 'showcase', shareUrl: string } | null}
 */
export function extractSupademoDemo(text = "") {
  const value = String(text || "").trim();
  if (!value) return null;

  // Share / custom-domain links, e.g.
  // https://app.supademo.com/demo/…
  // https://help.aq-trials.com/demo/cmtjn…?utm_source=link
  const urlMatch = value.match(
    /https?:\/\/[^\s/]+\/(demo|showcase)\/([A-Za-z0-9_-]+)/i,
  );
  if (urlMatch?.[2]) {
    const type = urlMatch[1].toLowerCase() === "showcase" ? "showcase" : "demo";
    return {
      demoId: urlMatch[2],
      type,
      shareUrl: value.split(/\s/)[0],
    };
  }

  // Paste of id only (from Supademo Embed → Supademo.open('…')).
  if (SUPADEMO_BARE_ID_REGEX.test(value) && !/guidde|playbooks/i.test(value)) {
    return {
      demoId: value,
      type: "demo",
      shareUrl: `https://app.supademo.com/demo/${value}`,
    };
  }

  return null;
}

export function stripGuiddeUrls(text = "") {
  return String(text || "")
    .replace(GUIDDE_SHARE_REGEX, "")
    .replace(GUIDDE_EMBED_REGEX, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

export function stripSupademoUrls(text = "") {
  return String(text || "")
    .replace(SUPADEMO_URL_REGEX, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function firstVideoCandidate(faq = {}) {
  return (
    String(faq.videoLink || faq.videoUrl || faq.video || "").trim() ||
    String(faq.answer || "").trim() ||
    ""
  );
}

/**
 * Resolve FAQ video for playback.
 * Prefers Supademo (popup SDK); falls back to Guidde iframe embed.
 */
export function resolveFaqVideo(faq = {}) {
  const candidate = firstVideoCandidate(faq);
  const fromField = String(
    faq.videoLink || faq.videoUrl || faq.video || "",
  ).trim();
  const rawAnswer = faq.answer || "";
  const isHtml = /<\/?[a-z][\s\S]*>/i.test(rawAnswer);

  const supademo =
    extractSupademoDemo(fromField) ||
    extractSupademoDemo(candidate) ||
    extractSupademoDemo(rawAnswer);

  if (supademo) {
    return {
      provider: "supademo",
      demoId: supademo.demoId,
      demoType: supademo.type,
      shareUrl: supademo.shareUrl,
      embedUrl: null,
      hasVideo: true,
      displayAnswer: isHtml
        ? rawAnswer
        : stripSupademoUrls(stripGuiddeUrls(rawAnswer)),
    };
  }

  const shareUrl =
    extractGuiddeVideoUrl(fromField) ||
    extractGuiddeVideoUrl(candidate) ||
    extractGuiddeVideoUrl(rawAnswer) ||
    null;
  const embedUrl = shareUrl ? toGuiddeEmbedUrl(shareUrl) : null;

  return {
    provider: embedUrl ? "guidde" : null,
    demoId: null,
    demoType: null,
    shareUrl,
    embedUrl,
    hasVideo: Boolean(embedUrl),
    displayAnswer:
      embedUrl && !isHtml ? stripGuiddeUrls(rawAnswer) : rawAnswer,
  };
}

function ensureSupademoScript() {
  if (typeof document === "undefined") return Promise.resolve();
  if (typeof window !== "undefined" && typeof window.Supademo?.open === "function") {
    return Promise.resolve();
  }
  const existing = document.querySelector('script[data-faq-supademo="1"]');
  if (existing) {
    return new Promise((resolve) => {
      if (typeof window.Supademo?.open === "function") {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => resolve(), { once: true });
    });
  }
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://script.supademo.com/supademo.js";
    script.async = true;
    script.dataset.faqSupademo = "1";
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
}

/**
 * Play a resolved FAQ video.
 * Supademo → popup overlay; Guidde → caller opens iframe modal via onGuiddePlay.
 * @returns {Promise<'supademo' | 'guidde' | 'none'>}
 */
export async function playFaqVideo(video, { onGuiddePlay } = {}) {
  if (!video?.hasVideo && !video?.demoId && !video?.embedUrl) return "none";

  if (video.provider === "supademo" && video.demoId) {
    await ensureSupademoScript();
    const open = typeof window !== "undefined" ? window.Supademo?.open : null;
    if (typeof open === "function") {
      if (video.demoType === "showcase") {
        open(video.demoId, { type: "showcase" });
      } else {
        open(video.demoId);
      }
      return "supademo";
    }
    if (video.shareUrl) {
      window.open(video.shareUrl, "_blank", "noopener,noreferrer");
      return "supademo";
    }
    return "none";
  }

  if (video.embedUrl && typeof onGuiddePlay === "function") {
    onGuiddePlay({
      embedUrl: video.embedUrl,
      shareUrl: video.shareUrl,
      title: video.title,
    });
    return "guidde";
  }

  return "none";
}
